import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDataGridTextFilter } from '../../../DataGrid/filters/useDataGridTextFilter.jsx';
import { dataGridTextSorter } from '../../../DataGrid/sorters/DataGridSorters.jsx';
import DataGrid from '../../../DataGrid/DataGrid.jsx';
import { CollapseBlock, QuestionModal, Spinner } from '../../../index.jsx';
import { WorkshopsListTableSettings } from './WorkshopsTableSettings.jsx';
import { SeriousDeleteQuestion } from '../../../DeleteQuestion/SeriousDeleteQuestion.jsx';

const WorkshopsTable = ({
  workshops,
  onDelete,
  onDetailClick,
  onRowClick,
  onEditClick,
  onShowDatesClick,
  onSignOnClick,
  isArchivedEvent,
}) => {
  const { t } = useTranslation();
  const nameTextFilterProps = useDataGridTextFilter('name');
  const displayNameTextFilterProps = useDataGridTextFilter([
    'lecturer',
    'displayname',
  ]);

  const [rowToBeDeleted, setRowToBeDeleted] = useState(null);

  const columns = useMemo(
    () => [
      {
        title: t('Názov'),
        dataIndex: 'name',
        ellipsis: false,
        sorter: dataGridTextSorter('name'),
        ...nameTextFilterProps,
      },
      {
        title: t('Lektor'),
        dataIndex: ['lecturer', 'displayname'],
        ellipsis: false,
        sorter: dataGridTextSorter('lecturer.displayname'),
        ...displayNameTextFilterProps,
      },
      {
        title: t('Počet termínov'),
        dataIndex: 'dates_count',
        ellipsis: false,
        sorter: (a, b) => Number(a?.dates_count ?? 0) - Number(b?.dates_count ?? 0),
        render: (_, record) => Number(record?.dates_count ?? 0),
      },
    ],
    [t, nameTextFilterProps, displayNameTextFilterProps],
  );

  if (!workshops) {
    return <Spinner />;
  }

  return (
    <CollapseBlock>
      <DataGrid
        rowKey={(workshop) => workshop.id}
        columns={columns}
        dataSource={workshops || []}
        onRowClick={onRowClick}
        customSettings={(row) => (
          <WorkshopsListTableSettings
            workshop={row}
            onShowDatesClick={onShowDatesClick}
            onDelete={(workshop) => setRowToBeDeleted(workshop.id)}
            onEditClick={onEditClick}
            onDetailClick={onDetailClick}
            onSignOnClick={onSignOnClick}
            isArchivedEvent={isArchivedEvent}
          />
        )}
      />
      <QuestionModal
        chosenPositive={() => {
          onDelete(rowToBeDeleted);
          setRowToBeDeleted(null);
        }}
        question={
          <SeriousDeleteQuestion
            firstText={t('Naozaj chcete vymazať tento seminár?')}
            mainNotice={t(
              'Po vymazaní seminára sa vymažú aj VŠETKY jeho termíny, ako aj zoznam účastníkov!',
            )}
            lastText={t(
              'Dôkladne zvážte tento krok. Operácia sa nedá vrátiť späť!',
            )}
          />
        }
        visible={rowToBeDeleted !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeleted(null)}
        headerTitle={t('Vymazať položku')}
      />
    </CollapseBlock>
  );
};

WorkshopsTable.propTypes = {
  workshops: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onCreateClick: PropTypes.func,
  onRowClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onShowDatesClick: PropTypes.func,
  isAdmin: PropTypes.bool,
  onSignOnClick: PropTypes.func,
  isArchivedEvent: PropTypes.bool,
};

export default WorkshopsTable;
